import React, { useState } from "react";
import {
  Toolbar,
  IconButton,
  Typography,
  Box,
  useTheme,
  styled,
  AppBar,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { drawerWidth } from "./CommonDrawer";
import useResponsive from "../hooks/useResponsive";
import WorkLog from "../components/Timer/WorkLog";
import LogoutDialog from "../components/LogoutDialog";
import LogoutIcon from '../assets/Logout.svg?react';
import { getAppName } from "../utils/commonMethods";
import { getTenantConfig } from "../tenantConfig";

interface HeaderProps {
  onMenuClick: () => void;
  onLogout: () => void;
  username: string;
  open: boolean;
  title: string;
}
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!open && {
    width: `calc(100% - ${66}px + 1px)`,
  }),
}));

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  onLogout,
  username,
  open,
  title,
}) => {
  const theme = useTheme();
  const { isTablet, isMobile } = useResponsive();
  const [isLogout, setIsLogout] = useState<boolean>(false);
  const dismissModal = () => {
    setIsLogout(false);
  }

  const getAppBarContent = () => {
    const iconColor = theme.palette.primary.main;
    const { logoDrawerSmall, displayName } = getTenantConfig(getAppName());
    return (
      <>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ mr: "10px" }}
          >
            <MenuIcon fontSize="large" />
          </IconButton>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isTablet && (
              <img
                src={logoDrawerSmall}
                alt={`${displayName} Logo`}
                style={{ height: 50 }}
              />
            )}
            {isMobile && (
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: "17px",
                    fontFamily: "Nunito Sans Regular700",
                    display: "flex",
                    color: theme.palette.greyColors.dark,
                  }}
                >
                  {title}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <WorkLog />
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mr: 2,
                }}
              >
                <Box
                  sx={{
                    width: 25,
                    height: 25,
                    borderRadius: "50%",
                    border: `2px solid ${theme.palette.primary.main}`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <PersonIcon color="primary" />
                </Box>
                <Typography
                  sx={{
                    mt: "2px",
                    fontSize: "10px",
                    fontFamily: "Nunito Sans SemiBold",
                    height: "12px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    // maxWidth: "60px"
                  }}
                >
                  {username}
                </Typography>
              </Box>
              {!isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="logout"
                  onClick={() => { setIsLogout(true) }}
                  sx={{
                    display: "flex",
                    alignSelf: "flex-start",
                    p: 0,
                  }}
                  disableRipple
                >
                  <LogoutIcon style={{ color: iconColor, width: 22, height: 22 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Toolbar>
        <Box
          sx={{
            height: isMobile ? "2px" : "3px",
            backgroundColor: theme.palette.primary.main,
            width: "100%",
          }}
        />
        <LogoutDialog visibility={isLogout} dismissModal={dismissModal} />
      </>
    );
  };

  return isTablet || isMobile ? (
    <AppBar elevation={0} color="secondary">
      {getAppBarContent()}
    </AppBar>
  ) : (
    <StyledAppBar elevation={0} color="secondary" position="fixed" open={open}>
      {getAppBarContent()}
    </StyledAppBar>
  );
};

export default Header;
